/**
 * Small shared React hooks for the Resales-Online landing prototype.
 *
 * Kept in one place so every component imports the same implementation:
 *   - useClickOutside — run a callback when a click/tap lands outside an element
 *     (closes the "Sent on" date dropdown and the country-code dropdown).
 *   - useIsMobile — track a max-width media query so the UI can switch *structure*
 *     (not just CSS) between the desktop and mobile Figma frames.
 */
import { useEffect, useState } from 'react';

/**
 * Calls `onOutside` whenever a mousedown happens outside the referenced element.
 * @param {React.RefObject} ref       ref attached to the popover's root element
 * @param {() => void}      onOutside callback fired on an outside click
 */
export function useClickOutside(ref, onOutside) {
    useEffect(() => {
        function handler(e) {
            if (ref.current && !ref.current.contains(e.target)) onOutside();
        }
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [ref, onOutside]);
}

/**
 * Returns true while the viewport is at or below `maxWidth`. Updates live on
 * resize/orientation change. Mirrors the mobile breakpoint (767px) used by the
 * mobile Figma frame (`71:17647`).
 * @param {number} maxWidth breakpoint in px (default 767)
 * @returns {boolean}
 */
export function useMediaQuery(query) {
    const read = () => typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia(query).matches;
    const [matches, setMatches] = useState(read);
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        const mq = window.matchMedia(query);
        const handler = (e) => setMatches(e.matches);
        setMatches(mq.matches);
        if (mq.addEventListener) mq.addEventListener('change', handler);
        else mq.addListener(handler); // Safari < 14
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', handler);
            else mq.removeListener(handler);
        };
    }, [query]);
    return matches;
}

export function useIsMobile(maxWidth = 767) {
    return useMediaQuery(`(max-width: ${maxWidth}px)`);
}
